import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Modal from '../../components/shared/Modal';
import { useConfirm } from '../../components/shared/Confirm';
import {
  METODOS, PERIODICIDADES, ROJO, AMBAR,
  money, hoy, fmtFecha, reglaRenovacion, sumarPeriodo, FORM_VACIO,
} from '../../utils/finanzas';

/**
 * Traduce un fallo del servidor a algo accionable.
 *
 * Los dos casos reales son de despliegue incompleto, y sin esto los dos se
 * veían igual: un mensaje genérico que no dice qué falta.
 *   404 sin cuerpo     -> el backend no tiene la ruta (falta subir finanzas.js)
 *   columna desconocida -> falta correr el cambio de esquema
 */
function motivoError(err, generico) {
  const status  = err.response?.status;
  const detalle = String(err.response?.data?.error || '');

  if (/unknown column|serie_anulada/i.test(detalle)) {
    return 'Falta correr en la base de datos el cambio que agrega la columna serie_anulada a plataforma_movimientos.';
  }
  if (status === 404 && !detalle) {
    return 'El servidor todavía no tiene esta función: falta subir routes/finanzas.js y reiniciar la aplicación.';
  }
  return detalle || generico;
}

/**
 * Gastos fijos: una fila por compromiso, no por renovación.
 *
 * Cada renovación es un movimiento propio, y es lo que aparece en la pestaña
 * Gastos cuando se marca pagado. Aquí se ve el conjunto: la regla de
 * renovación, cuánto lleva pagado y cuál es el próximo vencimiento.
 */
export default function GastosFijos({ categorias, proveedores, esMovil, notificar, onGuardado, diasAviso = 5 }) {
  const [series, setSeries]     = useState([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto]   = useState(false);
  const [editando, setEditando] = useState(null);   // id del movimiento pendiente
  const [guardando, setGuardando] = useState(false);
  const [pagando, setPagando]   = useState(null);
  const [form, setForm] = useState({ ...FORM_VACIO, clase: 'fijo' });
  const [fallo, setFallo] = useState('');
  const confirmar = useConfirm();

  const cats = categorias.filter(c => c.tipo === 'gasto');

  // Fallo que hay que leer: modal con un solo boton, con el dato tecnico a la
  // vista para poder reportarlo sin tener que abrir las herramientas del navegador.
  const avisarFallo = (titulo, mensaje, tecnico) => confirmar({
    titulo, mensaje, detalle: tecnico,
    aceptar: 'Entendido', soloAceptar: true, peligro: true,
  });

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get('/finanzas/gastos-fijos');
      const lista = r.data.series || [];
      setSeries(lista);
      setFallo('');
      // Se devuelve para que quien borra pueda comprobar que de verdad se fue.
      return lista;
    } catch (err) {
      // Sin esto la pestaña mostraba "Aún no hay gastos fijos registrados"
      // cuando en realidad la consulta fallo: parecia que no guardaba nada.
      const motivo = motivoError(err, 'No se pudieron cargar los gastos fijos');
      notificar(motivo, true);
      setFallo(motivo);
      setSeries([]);
      return [];
    } finally { setCargando(false); }
  }, [notificar]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ ...FORM_VACIO, clase: 'fijo', periodicidad: 'mensual' });
    setAbierto(true);
  };

  const cerrar = () => { setAbierto(false); setEditando(null); };

  const editar = (s) => {
    // Se edita la ocurrencia pendiente: es la única que aún puede cambiar.
    if (!s.pendiente) return notificar('Este gasto no tiene una renovación pendiente que editar', true);
    setEditando(s.pendiente.id);
    setForm({
      ...FORM_VACIO,
      clase: 'fijo',
      descripcion: s.descripcion,
      monto: String(s.pendiente.monto),
      // El inicio del ciclo, no su vencimiento: con periodicidad el vencimiento
      // se recalcula al guardar, y partir de el correria la fecha un periodo
      // mas en cada edicion.
      fecha: s.pendiente.fecha || s.fecha_compra || hoy(),
      fecha_vencimiento: s.pendiente.fecha_vencimiento || '',
      periodicidad: s.periodicidad || '',
      metodo_pago: s.metodo_pago || '',
      notas: s.notas || '',
    });
    setAbierto(true);
  };

  const guardar = async () => {
    if (!form.descripcion.trim()) return notificar('Escribe el nombre del gasto', true);
    if (!Number(form.monto))      return notificar('El valor debe ser mayor que cero', true);

    // El primer vencimiento es el dia de la compra: estos servicios se pagan
    // por adelantado, asi que la primera cuota se debe el mismo dia. El periodo
    // se suma al aprobar ese pago, no antes. Sin periodicidad hay que escribirlo.
    const vencimiento = form.periodicidad ? form.fecha : form.fecha_vencimiento;
    if (!vencimiento) {
      return notificar(form.periodicidad
        ? 'Indica la fecha de compra: desde ella se cuentan las renovaciones'
        : 'Un gasto fijo necesita fecha de vencimiento', true);
    }

    setGuardando(true);
    try {
      const cuerpo = {
        ...form, tipo: 'gasto', clase: 'fijo',
        monto: Number(form.monto),
        fecha: form.fecha || vencimiento,
        fecha_vencimiento: vencimiento,
      };
      if (editando) await api.put(`/finanzas/movimientos/${editando}`, cuerpo);
      else          await api.post('/finanzas/movimientos', cuerpo);
      notificar(editando ? 'Gasto fijo actualizado' : 'Gasto fijo registrado');
      cerrar(); cargar(); onGuardado();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo guardar', true);
    } finally { setGuardando(false); }
  };

  const pagar = async (s) => {
    if (!s.pendiente) return;
    setPagando(s.id);
    try {
      const r = await api.patch(`/finanzas/movimientos/${s.pendiente.id}/pagado`, { pagado: true });
      notificar(r.data?.renovados
        ? 'Pagado. Ya aparece en Gastos y se creó el siguiente vencimiento.'
        : 'Pagado. Ya aparece en Gastos.');
      cargar(); onGuardado();
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo registrar el pago', true);
    } finally { setPagando(null); }
  };

  // Da de baja el gasto fijo completo. Lo ya pagado no se toca: sigue en
  // Gastos y en los totales del periodo. Lo que desaparece es el compromiso
  // hacia adelante: deja de renovarse y de avisar.
  const eliminar = async (s) => {
    if (!await confirmar({
      titulo: 'Eliminar gasto fijo',
      mensaje: `¿Eliminar el gasto fijo "${s.descripcion}"?`,
      detalle: s.pagadas
        ? `Dejará de renovarse y de avisar. Los ${s.pagadas} pago(s) ya registrados ` +
          `(${money(s.total_pagado)}) se conservan en Gastos.`
        : 'Todavía no tiene pagos registrados, así que se elimina por completo.',
      aceptar: 'Eliminar',
      peligro: true,
    })) return;
    try {
      // POST y no DELETE: ver la nota de los verbos en routes/finanzas.js.
      const r = await api.post(`/finanzas/gastos-fijos/${s.id}/baja`);

      // No basta con que el servidor conteste bien: se comprueba que la fila
      // desaparecio de verdad. Un "eliminado" que deja el gasto en pantalla es
      // peor que un error, porque no hay nada que investigar.
      const restantes = await cargar();
      onGuardado();

      if (restantes.some(x => String(x.id) === String(s.id))) {
        await avisarFallo('El gasto sigue en la lista',
          'El servidor respondió correctamente, pero el gasto no desapareció.',
          `Respuesta ${r.status}: ${JSON.stringify(r.data)}`);
      } else {
        notificar(r.data?.message || 'Gasto fijo eliminado');
      }
    } catch (err) {
      // En un modal y no en un aviso que se borra solo: el aviso duraba 4,5
      // segundos y se podia perder sin llegar a leerlo, con lo que el fallo
      // parecia no existir.
      await avisarFallo('No se pudo eliminar el gasto fijo',
        motivoError(err, 'El servidor rechazó la operación.'),
        `${err.response?.status || 'sin respuesta'} · POST /finanzas/gastos-fijos/${s.id}/baja`);
    }
  };

  const colorVence = (dias) => {
    if (dias == null) return 'var(--color-text-muted)';
    if (dias < 0) return ROJO;
    return dias <= diasAviso ? AMBAR : 'var(--color-text)';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
        <p style={{ margin: 0, fontSize: '0.83rem', color: 'var(--color-text-muted)', maxWidth: '62ch', lineHeight: 1.6 }}>
          Cada gasto fijo se registra una sola vez. Al marcarlo pagado queda como
          registro en <strong>Gastos</strong> y se crea automáticamente el vencimiento siguiente.
          Al eliminarlo deja de renovarse, pero los pagos ya registrados se conservan.
        </p>
        <button className="btn btn-primary" onClick={abrirNuevo}>+ Registrar gasto fijo</button>
      </div>

      <div className="card" style={{ padding: 0, minWidth: 0 }}>
        {cargando ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
        ) : fallo ? (
          // El listado fallo. Decirlo, en vez de mostrar el vacio de siempre:
          // si no, cada gasto que se guarde parece perderse.
          <div style={{ padding: '2.5rem 2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>⚠️</div>
            <div style={{ color: 'var(--color-error)', fontWeight: 600, marginBottom: 6 }}>
              No se pudo cargar la lista
            </div>
            <p style={{ margin: '0 auto', maxWidth: '52ch', fontSize: '0.84rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
              {fallo} Lo que hayas guardado no se perdió: sigue en la base de datos y
              aparecerá aquí en cuanto se resuelva.
            </p>
            <button className="btn btn-secondary" style={{ marginTop: 14 }} onClick={cargar}>Reintentar</button>
          </div>
        ) : series.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Aún no hay gastos fijos registrados
          </div>
        ) : (
          <div className="table-wrapper" style={{ border: 0 }}>
            <table style={{ width: '100%', minWidth: esMovil ? 640 : 920 }}>
              <thead>
                <tr>
                  <th>Gasto</th>
                  {!esMovil && <th>Descripción</th>}
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  {!esMovil && <th>Fecha compra</th>}
                  <th>Renovación</th>
                  <th style={{ textAlign: 'right' }}>Pagado</th>
                  <th className="col-acciones">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {series.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontSize: '0.85rem' }}>
                      <div style={{ fontWeight: 600 }}>{s.descripcion}</div>
                      {s.proveedor && (
                        <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)' }}>{s.proveedor}</div>
                      )}
                    </td>
                    {!esMovil && (
                      <td style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', maxWidth: 300 }}>
                        {s.notas || s.categoria || '—'}
                      </td>
                    )}
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: ROJO }}>
                      {money(s.monto)}
                    </td>
                    {!esMovil && (
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{fmtFecha(s.fecha_compra)}</td>
                    )}
                    <td style={{ fontSize: '0.82rem' }}>
                      <div>{s.regla}</div>
                      {s.pendiente?.fecha_vencimiento && (
                        <div style={{ fontSize: '0.74rem', color: colorVence(s.pendiente.dias) }}>
                          Próximo: {fmtFecha(s.pendiente.fecha_vencimiento)}
                          {s.pendiente.dias != null && (s.pendiente.dias < 0
                            ? ` · vencido hace ${Math.abs(s.pendiente.dias)} d`
                            : ` · en ${s.pendiente.dias} d`)}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600 }}>{money(s.total_pagado)}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                        {s.pagadas} {s.pagadas === 1 ? 'renovación' : 'renovaciones'}
                      </div>
                    </td>
                    <td className="col-acciones">
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {s.pendiente && (
                          <button className="btn btn-primary btn-icon" title="Registrar el pago de esta renovación"
                                  disabled={pagando === s.id} onClick={() => pagar(s)}>
                            {pagando === s.id ? '…' : '✓'}
                          </button>
                        )}
                        <button className="btn btn-secondary btn-icon" title="Editar la renovación pendiente"
                                disabled={!s.pendiente} onClick={() => editar(s)}>✎</button>
                        <button className="btn btn-ghost btn-icon"
                                title="Eliminar el gasto fijo (los pagos ya registrados se conservan)"
                                onClick={() => eliminar(s)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {abierto && (
        <Modal
          titulo={`${editando ? 'Editar' : 'Registrar'} gasto fijo`}
          ancho={520}
          onCerrar={cerrar}
          pie={<>
            <button className="btn btn-ghost" onClick={cerrar} disabled={guardando}>Cancelar</button>
            <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Guardar gasto fijo'}
            </button>
          </>}
        >
          <div>
            <div className="form-group">
              <label className="form-label">Nombre del gasto</label>
              <input className="form-input" placeholder="Ej. Unlimited Web Hosting"
                     value={form.descripcion}
                     onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Descripción</label>
              <textarea className="form-input" rows={2}
                        placeholder="Hosting donde se almacena el backend de la aplicación"
                        value={form.notas}
                        onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Categoría</label>
                <select className="form-select" value={form.categoria_id}
                        onChange={e => setForm(f => ({ ...f, categoria_id: e.target.value }))}>
                  <option value="">Selecciona categoría</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Proveedor</label>
                <select className="form-select" value={form.proveedor_id}
                        onChange={e => setForm(f => ({ ...f, proveedor_id: e.target.value }))}>
                  <option value="">Sin proveedor</option>
                  {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Valor</label>
                <input type="number" min="0" step="1" className="form-input" placeholder="0"
                       value={form.monto}
                       onChange={e => setForm(f => ({ ...f, monto: e.target.value }))} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Fecha de compra</label>
                <input type="date" className="form-input" value={form.fecha}
                       onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Se renueva</label>
                <select className="form-select" value={form.periodicidad}
                        onChange={e => setForm(f => ({ ...f, periodicidad: e.target.value }))}>
                  {PERIODICIDADES.map(p => <option key={p.valor} value={p.valor}>{p.label}</option>)}
                </select>
              </div>

              {/* Con periodicidad el vencimiento no se escribe: sale de la fecha
                  de compra y de ahi en adelante lo corre sola cada renovacion.
                  Pedirlo a mano solo daba ocasion de contradecir la regla. */}
              {!form.periodicidad && (
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Fecha de vencimiento</label>
                  <input type="date" className="form-input" value={form.fecha_vencimiento}
                         onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
                </div>
              )}
            </div>

            {form.periodicidad && (
              <p style={{ margin: '-4px 2px 14px', fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                Vence el <strong>{fmtFecha(sumarPeriodo(form.fecha, form.periodicidad)) || '—'}</strong>{' '}
                y se renueva solo: <strong>{reglaRenovacion(form.periodicidad, form.fecha).toLowerCase()}</strong>.
                Cada vez que apruebes el pago se crea el vencimiento siguiente, y
                recibirás el aviso {diasAviso} días antes.
              </p>
            )}

            <div className="form-group">
              <label className="form-label">Método de pago</label>
              <select className="form-select" value={form.metodo_pago}
                      onChange={e => setForm(f => ({ ...f, metodo_pago: e.target.value }))}>
                <option value="">Selecciona método</option>
                {METODOS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <p style={{ margin: 0, fontSize: '0.76rem', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
              Recibirás un aviso {diasAviso} días antes de cada vencimiento. Al marcarlo
              pagado, el gasto queda registrado en la pestaña Gastos y se crea el siguiente.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}
